import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { LIBRARY_IMAGE } from "@/lib/content";
import SEO from "@/components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/password-reset/request`, { email });
      setSubmitted(true);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) || err.message
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen pt-32 pb-20 bg-ivory grid grid-cols-1 lg:grid-cols-12"
      data-testid="forgot-password-page"
    >
      <SEO
        title="Reset Your Password"
        description="Reset your Lone Star Counsel client-portal password. A secure reset link will be emailed to the address on file."
        path="/forgot-password"
        noindex
      />
      <aside className="hidden lg:block lg:col-span-5 panel-navy relative overflow-hidden">
        <img
          src={LIBRARY_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-navy/70"></div>
        <div className="relative h-full flex flex-col justify-end p-16 text-ivory">
          <div className="kicker kicker-ivory">Portal Access</div>
          <h2 className="mt-6 font-serif text-5xl leading-[1.0]">
            A quiet <span className="italic text-brass">reset.</span>
          </h2>
          <p className="mt-6 text-ivory/70 font-light leading-relaxed max-w-sm">
            We&rsquo;ll send a single-use link to the email you have on file.
            The link is good for one hour.
          </p>
        </div>
      </aside>

      <div className="lg:col-span-6 lg:col-start-7 flex items-center px-6 md:px-12 py-20">
        <div className="w-full max-w-md mx-auto">
          {submitted ? (
            <div data-testid="forgot-password-success">
              <div className="kicker">Check Your Inbox</div>
              <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.98]">
                If we know that email,
                <br />
                <span className="italic text-oxblood">a link is on its way.</span>
              </h1>
              <p className="mt-8 text-slate2 font-light leading-relaxed">
                Please check the inbox for{" "}
                <span className="font-cormorant italic text-navy">{email}</span>.
                The reset link expires in one hour and can only be used once.
              </p>
              <p className="mt-6 text-sm text-slate2 font-cormorant italic leading-relaxed">
                If the email doesn&rsquo;t arrive, look in your spam or
                promotions folder, or reach the firm at{" "}
                <a
                  href="mailto:info@LoneStarCounsel.com"
                  className="text-oxblood underline"
                >
                  info@LoneStarCounsel.com
                </a>
                .
              </p>
              <div className="mt-10">
                <Link
                  to="/login"
                  className="btn-primary"
                  data-testid="forgot-password-back-to-login"
                >
                  Return to Sign In
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="forgot-password-form">
              <div className="kicker">Password Reset</div>
              <h1 className="mt-4 font-serif text-5xl md:text-6xl leading-[0.98]">
                Reset your
                <br />
                <span className="italic text-oxblood">portal password.</span>
              </h1>
              <p className="mt-6 text-slate2 font-light leading-relaxed">
                Enter the email associated with your account and we&rsquo;ll
                send a secure reset link.
              </p>

              <div className="mt-12">
                <label className="block">
                  <div className="field-label mb-2">Email</div>
                  <input
                    className="field"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="forgot-password-email"
                  />
                </label>
              </div>

              {error && (
                <div
                  className="mt-6 text-oxblood font-serif italic text-sm"
                  data-testid="forgot-password-error"
                >
                  — {error}
                </div>
              )}

              <div className="mt-10 flex items-center justify-between">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-40"
                  data-testid="forgot-password-submit"
                >
                  {loading ? "Sending…" : "Send Reset Link"}
                </button>
                <Link
                  to="/login"
                  className="link-underline font-serif italic"
                  data-testid="forgot-password-back-link"
                >
                  Back to sign in
                </Link>
              </div>

              <div className="mt-12 hairline pt-6 text-xs uppercase tracking-[0.2em] text-slate2/70">
                Reset links expire after one hour · Single use only
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
